import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { useToast } from '@src/common/hooks/useToast';
import { getBrandDefaultPermitTypeUnit } from '@src/common/measurements/getBrandDefaultUnitSystem';
import { useListConditionsSheets } from '@src/orders/hooks/useListConditionsSheets';
import type {
  ConditionSheets,
  FormConditionSheet,
} from '@src/permits/completePermit/fields/ConditionSheets';
import {
  ConditionSheetsField,
  ShowInstructionsToClientField,
} from '@src/permits/fields/Fields';
import { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import { useCreatePermitType } from '@src/permitTypesManager/hooks/useCreatePermitType';
import type { ExistingPermitTypeDefinition } from '@src/permitTypesManager/PermitTypeDefinition';
import { PermitTypeManagementSheet } from '@src/permitTypesManager/PermitTypeManagementSheet';
import {
  CurrencyField,
  HighPriorityField,
  JurisdictionField,
  JurisdictionUrlField,
  NameField,
  ShowToClientField,
  TypeField,
  UnitSystemFields,
  ValidateExpirationDateField,
  ValidateExpirationTimeField,
} from '@src/permitTypesManager/permitTypeSections/PermitDefinitionFields';
import { PermitInstructionsField } from '@src/permitTypesManager/permitTypeSections/PermitInstructionsField';
import {
  PermitTypeFormMode,
  permitTypeToFormDefaults,
} from '@src/permitTypesManager/permitTypeToFormDefaults';
import { Navigation } from '@src/routing/Navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  duplicateFrom?: ExistingPermitTypeDefinition;
}

export const AddPermitType = ({ duplicateFrom }: Props = {}) => {
  const { conditionsSheets, isLoading } = useListConditionsSheets();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <LoadedAddPermitType
      conditionSheets={conditionsSheets}
      duplicateFrom={duplicateFrom}
    />
  );
};

const LoadedAddPermitType = ({
  conditionSheets,
  duplicateFrom,
}: {
  conditionSheets: ConditionSheets;
  duplicateFrom?: ExistingPermitTypeDefinition;
}) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();

  const emptyDefaults: AddPermitTypeFormInputs = {
    [NameField.fieldName]: '',
    [JurisdictionUrlField.fieldName]: undefined,
    [JurisdictionField.fieldName]: '',
    [TypeField.fieldName]: '',
    [CurrencyField.fieldName]: '',
    [HighPriorityField.fieldName]: false,
    [ValidateExpirationDateField.fieldName]: false,
    [ValidateExpirationTimeField.fieldName]: false,
    [PermitInstructionsField.fieldName]: undefined,
    [ConditionSheetsField.fieldName]: [],
    [ShowInstructionsToClientField.fieldName]: false,
    [ShowToClientField.fieldName]: true,
    [UnitSystemFields.fieldName]: getBrandDefaultPermitTypeUnit(),
  };

  const defaultValues: AddPermitTypeFormInputs = duplicateFrom
    ? permitTypeToFormDefaults(
        duplicateFrom,
        conditionSheets,
        PermitTypeFormMode.Duplicate,
      )
    : emptyDefaults;

  const form = useForm<AddPermitTypeFormInputs>({
    resolver: yupResolver(AddPermitTypeFormInputs),
    defaultValues,
    mode: 'onSubmit',
  });
  const { createPermitType } = useCreatePermitType({
    onSuccess: () => {
      showSuccessToast(t('permitTypesManager.addPermitType.success'));
      navigate(
        `/${Navigation.settings.PATH}?${Navigation.settings.searchParamKey}=0`,
      );
    },
    onError: () => {
      showErrorToast(t('permitTypesManager.addPermitType.generalError'));
    },
    onAlreadyExistsError: () => {
      showErrorToast(t('permitTypesManager.existentPermitError'));
    },
  });

  const handleSubmitAction = () => {
    const values = form.getValues();
    values.conditionsSheets = values.conditionsSheets?.filter(
      (sheet: FormConditionSheet) => sheet.sendToClient,
    );
    createPermitType(values);
  };

  const jurisdiction = form.watch(JurisdictionField.fieldName);

  useEffect(() => {
    const availableConditionSheets = conditionSheets
      .filterForJurisdiction(jurisdiction)
      .toDefaultForm();
    form.resetField(ConditionSheetsField.fieldName, {
      defaultValue: availableConditionSheets,
    });
  }, [jurisdiction, form, conditionSheets]);
  return (
    <PermitTypeManagementSheet
      form={form}
      pageTitle={t('permitTypesManager.addPermitType.title')}
      submitButtonText={t('create')}
      onSubmitAction={handleSubmitAction}
      conditionSheets={conditionSheets
        .filterForJurisdiction(jurisdiction)
        .toDefaultForm()}
    />
  );
};
